import { type FormEvent, useState } from 'react';
import { useApi, useConsulta, useSubida } from './api';
import { fechaCorta } from './formato';

/**
 * Lo vigente (arquitectura 2.23): la matriz de vías de importación por opción de compra y tipo de
 * vehículo, el probador de un auto y la lista de leyes y reglas aprobadas. La usan el Admin Lotero
 * (`/sourcing/importacion/vigente`, solo consulta) y Super Admin (`/sourcing/superadmin/vigente`).
 */

type EstadoCelda = 'disponible' | 'revisar' | 'no_disponible' | 'sin_regla' | 'no_aplica';
interface ViaCelda {
  tipo: string;
  nombre: string;
  color: 'importable' | 'revisar' | 'no_importable';
  arancelPorcentaje: string | null;
  motivos: string[];
  vigenteHasta: string | null;
  requisitos: string[];
  prohibiciones: string[];
  sinRegla: boolean;
  estimacion?: {
    totalMxn: string;
    igiMxn: string;
    ivaMxn: string;
    dtaMxn: string;
    /** 2.26: ISAN si la regla lo causa a este auto. */
    isanMxn?: string;
    causaIsan?: boolean | null;
  } | null;
  /** 2.26: claves de pedimento de la vía (Anexo 22, Ap. 2). */
  clavesPedimento?: { clave: string; nombre: string; conPedimento: boolean }[];
}
interface Celda {
  estado: EstadoCelda;
  vias: ViaCelda[];
}
interface Opcion {
  clave: string;
  nombre: string;
  descripcion: string;
  nota: string | null;
}
interface Matriz {
  fecha: string;
  columnas: { desde: number; hasta: number | null }[];
  filas: { opcion: Opcion; celdas: Celda[] }[];
}
interface Perfiles {
  origenes: string[];
  titulos: string[];
  clases: { clave: string; nombre: string }[];
}
export interface InstrumentoVigente {
  id: string;
  tipo: string;
  nombre: string;
  url: string;
  documentoUrl: string | null;
  fechaDof: string | null;
  ultimaReforma: string | null;
  vigenteDesde: string | null;
  vigenteHasta: string | null;
  venceEnDias: number | null;
  resumen: string | null;
  puntosClave: { tema: string; texto: string; cita: string }[];
  validadoAgenteEn: string | null;
}

const ESTADO: Record<EstadoCelda, { texto: string; clase: string; icono: string }> = {
  disponible: { texto: 'Disponible', clase: 'exito', icono: '✓' },
  revisar: { texto: 'Revisar', clase: 'aviso', icono: '!' },
  no_disponible: { texto: 'No disponible', clase: 'peligro', icono: '✕' },
  sin_regla: { texto: 'Sin regla publicada', clase: '', icono: '?' },
  no_aplica: { texto: 'No se importa', clase: '', icono: '—' },
};
const ORIGEN: Record<string, string> = {
  US: 'EE. UU.',
  CA: 'Canadá',
  MX: 'México',
  otro: 'Fuera de T-MEC',
};
const TITULO: Record<string, string> = {
  limpio: 'Limpio (clean)',
  rebuilt: 'Rebuilt',
  salvage: 'Salvage',
  non_repairable: 'Non-repairable',
  junk: 'Junk',
};
const TIPO_INSTRUMENTO: Record<string, string> = {
  ley: 'Ley',
  reglamento: 'Reglamento',
  decreto: 'Decreto',
  reglas: 'Reglas',
  tarifa: 'Tarifa',
  norma: 'Norma',
  aviso: 'Aviso',
};
const rango = (c: { desde: number; hasta: number | null }) =>
  c.hasta === null
    ? `${c.desde}+ años`
    : c.desde === c.hasta
      ? `${c.desde} años`
      : `${c.desde}–${c.hasta} años`;
const hoy = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });

/** Matriz de vías por opción de compra × antigüedad, filtrada por origen, título y clase. */
export function MatrizVigente({ base }: { base: string }) {
  const [origen, setOrigen] = useState('US');
  const [titulo, setTitulo] = useState('limpio');
  const [clase, setClase] = useState('auto');
  const [fecha, setFecha] = useState(hoy());
  const [abierta, setAbierta] = useState<{ fila: number; col: number } | null>(null);
  const perfiles = useConsulta<Perfiles>(`${base}/catalogos`);
  const q = new URLSearchParams({ origen, titulo, clase, fecha }).toString();
  const { datos: m, error } = useConsulta<Matriz>(`${base}/matriz?${q}`);
  const detalle = abierta && m ? m.filas[abierta.fila]?.celdas[abierta.col] : null;

  return (
    <section className="seccion">
      <header>
        <h2>Qué vías hay hoy</h2>
        <div style={{ flex: 1 }} />
        <label className="campo en-linea">
          <span>Origen del VIN</span>
          <select value={origen} onChange={(e) => setOrigen(e.target.value)}>
            {(perfiles.datos?.origenes ?? ['US']).map((o) => (
              <option key={o} value={o}>
                {ORIGEN[o] ?? o}
              </option>
            ))}
          </select>
        </label>
        <label className="campo en-linea">
          <span>Título</span>
          <select value={titulo} onChange={(e) => setTitulo(e.target.value)}>
            {(perfiles.datos?.titulos ?? ['limpio']).map((t) => (
              <option key={t} value={t}>
                {TITULO[t] ?? t}
              </option>
            ))}
          </select>
        </label>
        <label className="campo en-linea">
          <span>Clase</span>
          <select value={clase} onChange={(e) => setClase(e.target.value)}>
            {(perfiles.datos?.clases ?? []).map((c) => (
              <option key={c.clave} value={c.clave}>
                {c.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="campo en-linea">
          <span>Fecha de cruce</span>
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </label>
      </header>
      {error && <p className="error">{error}</p>}
      {m && (
        <div>
          <p className="subtitulo cuerpo leyenda-vias">
            {(['disponible', 'revisar', 'no_disponible', 'sin_regla', 'no_aplica'] as const).map(
              (k) => (
                <span key={k} className={`etiqueta ${ESTADO[k].clase}`}>
                  {ESTADO[k].icono} {ESTADO[k].texto}
                </span>
              ),
            )}
            <span>Cada celda: clave de pedimento y arancel (IGI). Ábrela para ver requisitos.</span>
          </p>
          <table className="tabla-vias">
            <thead>
              <tr>
                <th>Opción de compra · años del auto</th>
                {m.columnas.map((c) => (
                  <th key={c.desde} title={rango(c)}>
                    {c.hasta === null
                      ? `${c.desde}+`
                      : c.desde === c.hasta
                        ? c.desde
                        : `${c.desde}–${c.hasta}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {m.filas.map((f, i) => (
                <tr key={f.opcion.clave}>
                  <td title={f.opcion.descripcion}>
                    <strong>{f.opcion.nombre}</strong>
                  </td>
                  {f.celdas.map((c, j) => {
                    const e = ESTADO[c.estado];
                    const via = c.vias.find((v) => v.color === 'importable');
                    const arancel = via?.arancelPorcentaje;
                    const claves = via?.clavesPedimento?.map((k) => k.clave).join('/');
                    const completo = `${e.texto}${claves ? ` · ${claves}` : ''}${arancel ? ` · ${arancel} %` : ''}`;
                    return (
                      <td key={j}>
                        <button
                          type="button"
                          className={`etiqueta via-celda ${e.clase}`}
                          title={completo}
                          aria-label={`${f.opcion.nombre}, ${rango(m.columnas[j] ?? { desde: 0, hasta: null })}: ${completo}`}
                          onClick={() => setAbierta({ fila: i, col: j })}
                          disabled={c.estado === 'no_aplica'}
                        >
                          {e.icono}
                          {claves ? ` ${claves}` : ''}
                          {arancel ? ` ${arancel} %` : ''}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detalle && abierta && m && (
        <div className="cuerpo">
          <h3>
            {m.filas[abierta.fila]?.opcion.nombre} ·{' '}
            {rango(m.columnas[abierta.col] ?? { desde: 0, hasta: null })}
          </h3>
          <Vias vias={detalle.vias} />
          {m.filas[abierta.fila]?.opcion.nota && (
            <p className="subtitulo">{m.filas[abierta.fila]?.opcion.nota}</p>
          )}
        </div>
      )}
      <p className="subtitulo cuerpo">
        Sale de las reglas publicadas por Siniestrados con el mismo semáforo de cada auto. Es
        orientación: la importación la confirma tu agente aduanal.
      </p>
    </section>
  );
}

function Vias({ vias }: { vias: ViaCelda[] }) {
  return (
    <table>
      <thead>
        <tr>
          <th>Vía</th>
          <th>Pedimento</th>
          <th>Resultado</th>
          <th className="num">Arancel</th>
          <th>Vence</th>
          <th>Por qué</th>
        </tr>
      </thead>
      <tbody>
        {vias.map((v) => (
          <tr key={v.tipo}>
            <td>{v.nombre}</td>
            <td>
              {v.clavesPedimento?.length
                ? v.clavesPedimento.map((c) => (
                    <abbr key={c.clave} title={c.nombre} className="etiqueta">
                      {c.conPedimento ? c.clave : 'Sin pedimento'}
                    </abbr>
                  ))
                : '—'}
            </td>
            <td>
              {v.sinRegla
                ? 'Sin regla publicada'
                : v.color === 'importable'
                  ? 'Importable'
                  : v.color === 'revisar'
                    ? 'Revisar'
                    : 'No importable'}
            </td>
            <td className="num">{v.arancelPorcentaje ? `${v.arancelPorcentaje} %` : '—'}</td>
            <td>{v.vigenteHasta ? fechaCorta(v.vigenteHasta) : '—'}</td>
            <td className="celda-texto">
              {[
                ...v.motivos,
                ...v.requisitos.map((r) => `Requisito: ${r}`),
                ...v.prohibiciones.map((p) => `Prohibido: ${p}`),
              ].join(' · ') || '—'}
              {v.estimacion && (
                <div className="vin">
                  Contribuciones estimadas: ${Number(v.estimacion.totalMxn).toLocaleString('es-MX')}{' '}
                  MXN
                  {Number(v.estimacion.isanMxn ?? 0) > 0 &&
                    ` (incluye ISAN de $${Number(v.estimacion.isanMxn).toLocaleString('es-MX')}; no lo paga el comerciante en el ramo, por confirmar)`}
                  {v.estimacion.causaIsan === null && ' · la vía causa ISAN: falta la antigüedad'}
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface Decodificado {
  vin: string;
  digitoVerificadorValido: boolean;
  decodificado: {
    anio: number | null;
    marca: string | null;
    modelo: string | null;
    version: string | null;
    carroceria: string | null;
    motor: string | null;
    combustible: string | null;
    traccion: string | null;
    paisPlanta: string | null;
    pesoBruto?: string | null;
    avisos: string[];
  } | null;
  perfil: {
    clase: 'auto' | 'camioneta' | 'camioneta_trabajo' | null;
    combustible: string | null;
    pesoBrutoKg: number | null;
  };
}
interface ResultadoOpcion {
  opcion: Opcion;
  antiguedad: number;
  estado: EstadoCelda;
  vias: ViaCelda[];
}
const COMBUSTIBLE: Record<string, string> = {
  gasolina: 'Gasolina',
  diesel: 'Diésel',
  hibrido: 'Híbrido',
  electrico: 'Eléctrico',
  otro: 'Otro',
};
const vinValido = (v: string) => /^[A-HJ-NPR-Z0-9]{17}$/.test(v);

/**
 * Probar un auto (2.26.1): empieza por el VIN, obligatorio (escrito o leído por la IA de una foto
 * de la etiqueta); NHTSA vPIC da los datos de fábrica, la plataforma deduce clase, combustible y
 * peso, y prueba todas las opciones de compra con su clave de pedimento y su costo.
 */
export function ProbadorVigente({ base }: { base: string }) {
  const api = useApi();
  const subir = useSubida();
  const decodificarEn = base.includes('/superadmin/')
    ? '/inventory/superadmin/vin'
    : '/inventory/vin';
  const [vin, setVin] = useState('');
  const [datos, setDatos] = useState<Decodificado | null>(null);
  const [clase, setClase] = useState('auto');
  const [titulo, setTitulo] = useState('limpio');
  const [valor, setValor] = useState('');
  const [resultado, setResultado] = useState<{
    antiguedad: number | null;
    opciones: ResultadoOpcion[];
  } | null>(null);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const limpio = vin.replace(/[\s-]/g, '').toUpperCase();

  const decodificar = async (v: string) => {
    setError(null);
    setResultado(null);
    try {
      const d = await api<Decodificado>('GET', `${decodificarEn}/${v}`);
      setDatos(d);
      if (d.perfil.clase) setClase(d.perfil.clase);
    } catch (err) {
      setDatos(null);
      setError((err as Error).message);
    }
  };
  const cambiarVin = (texto: string) => {
    const v = texto.replace(/[\s-]/g, '').toUpperCase();
    setVin(v);
    if (vinValido(v)) void decodificar(v);
    else setDatos(null);
  };
  const leerFoto = async (archivo: Blob | null | undefined) => {
    if (!archivo) return;
    setError(null);
    setLeyendo(true);
    try {
      const r = await subir<{ vin: string; advertencias: string[] }>(`${base}/leer-vin`, archivo);
      cambiarVin(r.vin);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLeyendo(false);
    }
  };
  const probar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    try {
      setResultado(
        await api('POST', `${base}/probar`, {
          vin: limpio,
          titulo,
          clase,
          ...(valor && { valorAduanaMxn: valor }),
        }),
      );
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const d = datos?.decodificado;
  return (
    <section
      className="seccion"
      onPaste={(e) => {
        const archivo = [...e.clipboardData.items]
          .find((x) => x.type.startsWith('image/'))
          ?.getAsFile();
        if (archivo) {
          e.preventDefault();
          void leerFoto(archivo);
        }
      }}
    >
      <header>
        <h2>Probar un auto</h2>
      </header>
      <form
        className="cuerpo acciones"
        onSubmit={(e) => void probar(e)}
        style={{ flexWrap: 'wrap' }}
      >
        <label className="campo">
          <span>VIN</span>
          <input
            name="vin"
            value={vin}
            onChange={(e) => cambiarVin(e.target.value)}
            maxLength={20}
            required
            placeholder="17 caracteres, o pega la foto de la etiqueta"
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <label className="campo">
          <span>Foto de la etiqueta del VIN</span>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            disabled={leyendo}
            onChange={(e) => void leerFoto(e.target.files?.[0])}
          />
          <small className="subtitulo">
            {leyendo ? 'Leyendo con IA…' : 'O pega la imagen aquí (Ctrl+V)'}
          </small>
        </label>
        <label className="campo">
          <span>Título</span>
          <select value={titulo} onChange={(e) => setTitulo(e.target.value)}>
            {Object.entries(TITULO).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="campo" htmlFor="clase-probador">
          <span>Clase</span>
          <select
            id="clase-probador"
            aria-describedby="clase-del-vin"
            value={clase}
            onChange={(e) => setClase(e.target.value)}
          >
            <option value="auto">Auto</option>
            <option value="camioneta">Camioneta</option>
            <option value="camioneta_trabajo">Camioneta de trabajo</option>
          </select>
          {datos?.perfil.clase && (
            <small id="clase-del-vin" className="subtitulo">
              Sale del VIN
            </small>
          )}
        </label>
        <label className="campo">
          <span>Valor en aduana MXN (opcional)</span>
          <input
            inputMode="decimal"
            pattern="\d+(\.\d{1,2})?"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
          />
        </label>
        <button className="boton primario" disabled={!vinValido(limpio)}>
          Probar
        </button>
      </form>
      {error && <p className="error">{error}</p>}
      {datos && (
        <div className="cuerpo">
          <p>
            <span className="vin">{datos.vin}</span>
            {!datos.digitoVerificadorValido && (
              <span className="etiqueta aviso"> Dígito verificador no cuadra: revisa el VIN</span>
            )}
          </p>
          {d ? (
            <dl className="resumen">
              {[
                ['Vehículo', [d.anio, d.marca, d.modelo, d.version].filter(Boolean).join(' ')],
                ['Carrocería', d.carroceria],
                ['Motor', d.motor],
                [
                  'Combustible',
                  datos.perfil.combustible ? COMBUSTIBLE[datos.perfil.combustible] : d.combustible,
                ],
                [
                  'Peso bruto vehicular',
                  datos.perfil.pesoBrutoKg
                    ? `hasta ${datos.perfil.pesoBrutoKg.toLocaleString('es-MX')} kg`
                    : d.pesoBruto,
                ],
                ['Tracción', d.traccion],
                ['Planta', d.paisPlanta],
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={String(k)}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
            </dl>
          ) : (
            <p className="subtitulo">NHTSA no respondió; el año y el origen salen del VIN.</p>
          )}
          {d?.avisos.map((a) => (
            <p key={a} className="subtitulo">
              {a}
            </p>
          ))}
        </div>
      )}
      {resultado && (
        <div className="cuerpo">
          <p>{resultado.antiguedad ?? '—'} años de antigüedad a la fecha de cruce. Opciones:</p>
          <table>
            <tbody>
              {resultado.opciones.map((o) => (
                <tr key={o.opcion.clave}>
                  <td className="celda-texto">
                    <strong>{o.opcion.nombre}</strong>
                    <div className="vin">{o.opcion.descripcion}</div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`etiqueta ${ESTADO[o.estado].clase}`}
                      disabled={o.estado === 'no_aplica'}
                      onClick={() => setAbierta(abierta === o.opcion.clave ? null : o.opcion.clave)}
                    >
                      {ESTADO[o.estado].icono} {ESTADO[o.estado].texto}
                      {(() => {
                        const via = o.vias.find((v) => v.color === 'importable');
                        const claves = via?.clavesPedimento?.map((k) => k.clave).join('/');
                        return `${claves ? ` · ${claves}` : ''}${via?.arancelPorcentaje ? ` · ${via.arancelPorcentaje} %` : ''}`;
                      })()}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {resultado.opciones
            .filter((o) => o.opcion.clave === abierta)
            .map((o) => (
              <div key={o.opcion.clave}>
                <h3>{o.opcion.nombre}</h3>
                <Vias vias={o.vias} />
              </div>
            ))}
        </div>
      )}
    </section>
  );
}

/** Leyes, decretos y reglas aprobados, con su resumen en lenguaje sencillo y la fuente oficial. */
export function InstrumentosVigentes({
  instrumentos,
  avisos,
}: {
  instrumentos: InstrumentoVigente[];
  avisos: { id: string; nombre: string; vigenteHasta: string | null; venceEnDias: number | null }[];
}) {
  return (
    <section className="seccion">
      <header>
        <h2>Leyes, decretos y reglas vigentes</h2>
      </header>
      {avisos.map((a) => (
        <p key={a.id} className="etiqueta aviso cuerpo">
          ⚠ {a.nombre} vence el {a.vigenteHasta ? fechaCorta(a.vigenteHasta) : '—'} (en{' '}
          {a.venceEnDias} días)
        </p>
      ))}
      {!instrumentos.length ? (
        <p className="vacio">Aún no hay leyes ni reglas aprobadas por Siniestrados.</p>
      ) : (
        <div className="cuerpo">
          {instrumentos.map((i) => (
            <details key={i.id}>
              <summary>
                <strong>{i.nombre}</strong> · {TIPO_INSTRUMENTO[i.tipo] ?? i.tipo}
                {i.vigenteHasta && ` · vigente hasta ${fechaCorta(i.vigenteHasta)}`}
                {i.ultimaReforma && ` · última reforma ${fechaCorta(i.ultimaReforma)}`}
                {i.validadoAgenteEn && ' · ✓ validado con agente aduanal'}
              </summary>
              {i.resumen && <p>{i.resumen}</p>}
              {i.puntosClave.length > 0 && (
                <ul>
                  {i.puntosClave.map((p) => (
                    <li key={`${p.tema}-${p.cita}`}>
                      <strong>{p.tema}:</strong> {p.texto} <span className="vin">({p.cita})</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="subtitulo">
                <a href={i.url} target="_blank" rel="noreferrer">
                  Fuente oficial
                </a>
                {i.documentoUrl && (
                  <>
                    {' · '}
                    <a href={i.documentoUrl} target="_blank" rel="noreferrer">
                      Texto completo
                    </a>
                  </>
                )}
              </p>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}

/** Términos de Lo vigente (aduana, impuestos y títulos), con las claves de pedimento del catálogo. */
const GLOSARIO: [string, string][] = [
  [
    'Pedimento',
    'Declaración aduanal con la que el auto entra al país; la tramita el agente o agencia aduanal y lleva una clave según el tipo de importación.',
  ],
  [
    'Clave de pedimento',
    'Código del Anexo 22 de las RGCE que dice qué tipo de operación es (definitiva, temporal, regularización…).',
  ],
  [
    'Arancel (IGI)',
    'Impuesto general de importación: un porcentaje sobre el valor en aduana, según la vía y la antigüedad del auto.',
  ],
  [
    'Valor en aduana',
    'Base de los impuestos. En usados: el valor de un auto nuevo equivalente menos 30 % el primer año y 10 % por año siguiente, hasta 80 % (Ley Aduanera art. 78).',
  ],
  ['IVA', '16 % sobre el valor en aduana más el IGI, el DTA y, si aplica, el ISAN.'],
  ['DTA', 'Derecho de trámite aduanero: 8 al millar (0.8 %) del valor en aduana.'],
  [
    'ISAN',
    'Impuesto sobre automóviles nuevos: lo causa la importación definitiva de modelos del año o de los 10 anteriores por alguien que no es comerciante del ramo.',
  ],
  ['Prevalidación', 'Cuota fija por pedimento que se paga antes del despacho (RGCE regla 1.8.3).'],
  [
    'Decreto de vehículos usados',
    'Permite importar usados de ciertos años y orígenes (EE. UU., Canadá, México) con arancel reducido y sin permiso previo; tiene fecha de fin.',
  ],
  [
    'Franja o región fronteriza',
    'Franja fronteriza norte, Baja California, Baja California Sur, región parcial de Sonora, Cananea y Caborca: vía propia para residentes.',
  ],
  [
    'Régimen general',
    'Importar fuera del decreto: cualquier año y origen, con permiso previo de la Secretaría de Economía y arancel general de la LIGIE.',
  ],
  [
    'Permiso previo',
    'Autorización de la Secretaría de Economía para importar mercancía restringida, como los autos usados fuera del decreto.',
  ],
  [
    'Clásico',
    'Auto de 30 años o más que se importa sin permiso previo, pagando la tasa general de usados.',
  ],
  [
    'Origen del VIN (NIV)',
    'País donde se fabricó o ensambló el auto, según su número de serie; el decreto solo admite T-MEC (EE. UU., Canadá y México).',
  ],
  [
    'Título limpio, rebuilt y salvage',
    'Situación legal del auto en EE. UU.: limpio sin siniestro; salvage siniestrado; rebuilt reparado e inspeccionado. Algunas leyendas impiden importarlo.',
  ],
  [
    'Fecha de cruce',
    'Día en que el auto pasa la aduana: se usa la regla vigente ese día, no la de hoy.',
  ],
  [
    'RGCE',
    'Reglas Generales de Comercio Exterior del SAT; cambian cada año y por resoluciones de modificación publicadas en el DOF.',
  ],
  [
    'LIGIE',
    'Ley de los Impuestos Generales de Importación y de Exportación: su tarifa fija el arancel de cada fracción.',
  ],
  [
    'IMMEX',
    'Programa de la Secretaría de Economía para importar temporalmente, reparar o transformar y devolver al extranjero.',
  ],
];

export function GlosarioVigente({ base }: { base: string }) {
  const { datos } = useConsulta<{ claves: { clave: string; nombre: string }[] }>(
    `${base}/claves-pedimento`,
  );
  return (
    <section className="seccion">
      <header>
        <h2>Glosario</h2>
      </header>
      <dl className="cuerpo glosario">
        {GLOSARIO.map(([termino, significado]) => (
          <div key={termino}>
            <dt>{termino}</dt>
            <dd>{significado}</dd>
          </div>
        ))}
        {datos?.claves.map((c) => (
          <div key={c.clave}>
            <dt>{c.clave === 'BANJERCITO' ? 'Permiso Banjercito' : `Clave ${c.clave}`}</dt>
            <dd>{c.nombre}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Pantalla completa para el lotero: solo consulta lo aprobado. */
export function LoVigente({ base }: { base: string }) {
  const { datos, error } = useConsulta<{
    instrumentos: InstrumentoVigente[];
    avisos: {
      id: string;
      nombre: string;
      vigenteHasta: string | null;
      venceEnDias: number | null;
    }[];
  }>(`${base}/instrumentos`);
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Lo vigente</h1>
          <div className="subtitulo">
            Qué tipo de importación aplica a cada opción de compra y tipo de vehículo, y las leyes
            de donde sale
          </div>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      <MatrizVigente base={base} />
      <ProbadorVigente base={base} />
      {datos && <InstrumentosVigentes instrumentos={datos.instrumentos} avisos={datos.avisos} />}
      <GlosarioVigente base={base} />
    </>
  );
}
